import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { Bell, CheckCheck, Inbox } from "lucide-react";
import { api } from "@/lib/api.ts";
import { keys, useApiMutation, useApiQuery } from "@/lib/query.ts";
import { fmtAgo } from "@/lib/utils.ts";
import { Button } from "@/components/ui/button.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { cn } from "@/lib/utils.ts";

export interface NotificationItem {
  id: number;
  subject: string;
  body: string | null;
  status: string;
  createdAt: string;
  readAt: string | null;
  payload?: Record<string, unknown> | null;
}

type Raw = NotificationItem[] | { items: NotificationItem[]; total?: number; unread?: number } | null | undefined;

export function normaliseNotifications(raw: Raw): NotificationItem[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return Array.isArray(raw.items) ? raw.items : [];
}

export function useUnreadNotifications() {
  const q = useApiQuery<Raw>([...keys.notifications, "me"], "/api/me/notifications", { query: { unread: 1 }, refetchInterval: 60_000 });
  const items = useMemo(() => normaliseNotifications(q.data), [q.data]);
  const unread = useMemo(() => items.filter((n) => !n.readAt && n.status !== "read").length, [items]);
  return { ...q, items, unread };
}

export function NotificationsPopover({ className }: { className?: string }) {
  const { items, unread, isError } = useUnreadNotifications();
  const markAll = useApiMutation<void, unknown>({ fn: () => api.post("/api/me/notifications/read"), invalidate: [keys.notifications] });
  const markOne = useApiMutation<number, unknown>({ fn: (id) => api.post("/api/me/notifications/read", { ids: [id] }), invalidate: [keys.notifications] });
  const recent = items.slice(0, 8);
  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className={cn("relative", className)} aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}>
              <Bell />
              {unread > 0 && (
                <span className="absolute top-1.5 right-1.5 grid min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] leading-4 font-semibold text-primary-foreground" aria-hidden="true">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Notifications</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <div className="text-sm font-medium">Notifications</div>
          <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" disabled={unread === 0 || markAll.isPending} onClick={() => markAll.mutate()}>
            <CheckCheck className="size-3.5" /> Mark all read
          </Button>
        </div>
        <Separator />
        {isError || recent.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <Inbox className="size-6 text-muted-foreground" />
            <div className="text-sm font-medium">{isError ? "Notifications unavailable" : "You're all caught up"}</div>
            <div className="text-xs text-muted-foreground">{isError ? "We couldn't load your notifications right now." : "New alerts and updates will appear here."}</div>
          </div>
        ) : (
          <ScrollArea className="max-h-[360px]">
            <ul className="divide-y">
              {recent.map((n) => {
                const isUnread = !n.readAt && n.status !== "read";
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      className="flex w-full gap-3 px-3 py-2.5 text-left transition-colors duration-150 hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                      onClick={() => isUnread && markOne.mutate(n.id)}
                    >
                      <span className={cn("mt-1.5 size-2 shrink-0 rounded-full", isUnread ? "bg-primary" : "bg-transparent")} aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className={cn("block truncate text-sm", isUnread ? "font-medium" : "text-foreground/80")}>{n.subject}</span>
                        {n.body && <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">{n.body}</span>}
                        <span className="mt-1 block text-[11px] text-muted-foreground">{fmtAgo(n.createdAt)}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}
        <Separator />
        <div className="p-1.5">
          <Button variant="ghost" size="sm" className="w-full justify-center text-xs" asChild>
            <Link to="/notifications">View all notifications</Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default NotificationsPopover;
